import React, {useState, useEffect} from 'react';
import {Context} from '../../context';
import Section from '../UI/Section';
import Card from '../UI/Card';
import LabelAndInput from '../UI/LabelAndInput';
import Button from '../UI/Button';
import Pagination from '../UI/Pagination';

import FareProductsSection from './FareProductsSection';
import {getDateString, dollarAmount, assetImgUrl} from '../../helperFunctions';
import {riderTypeSelect} from '../../settings';

export default function ManageCards() {
  const {
    account,
    content,
    updateTransitCard,
    getTicketName,
    localeText,
    addTransitCard,
    loading,
    fetchAccount
  } = React.useContext(Context);
  const [selectedCard, setSelectedCard] = useState(
    account?.transitAccounts?.[0].transitCards?.[0]
  );
  const [selectedCardScansPaginated, setSelectedCardScansPaginated] = useState(
    []
  );

  const [riderTypeOptions, setRiderTypeOptions] = useState([]);
  const SELECTED_CARD_SCAN_PER_PAGE = 5;

  const [newTransitCardNumber, setNewTransitCardNumber] = useState('');

  useEffect(() => {
    if (riderTypeSelect) {
      const products = content?.catalog?.products;

      if (Array.isArray(products) && products.length > 0) {
        const riderTypes = products
          .map(product => product?.properties?.riderType)
          .filter(riderType => riderType);
        if (riderTypes.length > 0) {
          setRiderTypeOptions([...new Set(riderTypes)]);
        }
      }
    }
  }, [content]);

  const nicknameChangeHandler = e => {
    setSelectedCard(prev => {
      return {...prev, nickname: e.target.value};
    });
  };

  const riderTypeChangeHandler = e => {
    setSelectedCard(prev => {
      return {...prev, riderType: e.target.value};
    });
  };

  const saveCardChanges = () =>
    updateTransitCard({
      uuid: selectedCard?.uuid,
      riderType: selectedCard?.riderType.toLowerCase(),
      nickname: selectedCard?.nickname
    });

  const addTransitCardHandler = async () => {
    const addTransitCardReturn = await addTransitCard(newTransitCardNumber);
    if (addTransitCardReturn?.errors) {
      return;
    }
    const updatedAccount = await fetchAccount();

    setSelectedCard(
      updatedAccount.transitAccounts[0].transitCards.find(
        transitCard =>
          Number(transitCard.cardNumber) === Number(newTransitCardNumber)
      )
    );
  };

  let manageCardsRightSide;
  if (selectedCard?.uuid === 'add') {
    manageCardsRightSide = (
      <div className='w-full flex gap-10'>
        <div className='w-96'>
          <LabelAndInput
            name='new card number'
            value={newTransitCardNumber}
            type='text'
            label={localeText(
              'rw_title_card_control_number',
              'Transit Card Number'
            )}
            onChange={e => setNewTransitCardNumber(e.target.value)}
          ></LabelAndInput>
          <Button className='w-full  mt-4' onClick={addTransitCardHandler}>
            {localeText('rw_button_register_card', 'Register Card')}
          </Button>
        </div>
        <div className='flex flex-col lg:flex-row gap-4'>
          {assetImgUrl(content, 'transitCardBack') && (
            <img
              alt={`${localeText(
                'rw_alt_transit_card_img',
                'Transit account card image'
              )}`}
              src={assetImgUrl(content, 'transitCardBack')}
              className='h-48 rounded-lg mt-5 hidden sm:inline-block'
            />
          )}
          {assetImgUrl(content, 'transitCardFront') && (
            <img
              alt={`${localeText(
                'rw_alt_transit_card_img',
                'Transit account card image'
              )}`}
              src={assetImgUrl(content, 'transitCardFront')}
              className='h-48 rounded-lg mt-5 hidden sm:inline-block'
            />
          )}
        </div>
      </div>
    );
  } else {
    manageCardsRightSide = (
      <div className='w-full'>
        <div className='w-full flex gap-8'>
          <Section
            heading={localeText('rw_header_info', 'Info')}
            className='w-96'
          >
            <LabelAndInput
              name='Name'
              value={selectedCard?.nickname}
              type='text'
              label={localeText('rw_title_transit_card_nickname', 'Card Name')}
              onChange={nicknameChangeHandler}
            ></LabelAndInput>
            <LabelAndInput
              name='Transit Card Number'
              value={selectedCard?.cardNumber ? selectedCard?.cardNumber : ' '}
              type='text'
              label={localeText(
                'rw_title_card_control_number',
                'Transit Card Number'
              )}
              className='my-4'
              inputClassName='border-0 outline-none py-0'
              readOnly={true}
            ></LabelAndInput>
            {riderTypeSelect && riderTypeOptions?.length > 0 ? (
              <LabelAndInput
                name='Rider Type'
                value={selectedCard?.riderType}
                type='select'
                label={localeText('rw_title_rider_type', 'Rider Type')}
                onChange={riderTypeChangeHandler}
              >
                {riderTypeOptions.map(riderTypeOption => {
                  if (!riderTypeOption) {
                    return null;
                  }
                  return (
                    <option
                      key={riderTypeOption}
                      value={riderTypeOption.toUpperCase()}
                    >
                      {localeText(
                        `rw_title_${riderTypeOption.toLowerCase()}`,
                        riderTypeOption
                      )}
                    </option>
                  );
                })}
              </LabelAndInput>
            ) : (
              <LabelAndInput
                name='Rider Type'
                value={
                  selectedCard?.riderType
                    ? localeText(
                        `rw_title_${selectedCard.riderType.toLowerCase()}`,
                        selectedCard.riderType
                      )
                    : ''
                }
                type='text'
                label={localeText('rw_title_rider_type', 'Rider Type')}
                className='my-4'
                inputClassName='border-0 outline-none py-0'
                readOnly={true}
              ></LabelAndInput>
            )}
            <Button
              className='w-full mt-4'
              loading={loading}
              onClick={saveCardChanges}
            >
              {localeText('rw_button_save', 'Save')}
            </Button>
          </Section>
        </div>
        <FareProductsSection
          {...{
            selectedCard
          }}
        />
        <Section
          heading={localeText('rw_header_usage_history', 'Usage History')}
        >
          <div className='w-fit'>
            <table
              //* Scans - DESKTOP View
              className='hidden sm:table'
            >
              <tbody>
                <tr>
                  <th className='text-left pr-4'>
                    {localeText('rw_title_fare_product', 'Fare Product')}
                  </th>
                  <th className='text-left pr-4'>
                    {localeText('rw_title_location', 'Location')}
                  </th>
                  <th className='text-left pr-4'>
                    {localeText('rw_title_date', 'Date')}
                  </th>
                  <th className='text-left pr-4'>
                    {localeText('rw_title_time', 'Time')}
                  </th>
                  <th className='text-left pr-4'>
                    {localeText('rw_title_amount', 'Amount')}
                  </th>
                </tr>
                {selectedCardScansPaginated &&
                  selectedCardScansPaginated.map((scan, i) => {
                    return (
                      <tr key={i} className='hover:bg-lightGray2'>
                        <td className='pr-4'>
                          {getTicketName(scan.productId)}
                        </td>
                        <td className='pr-4'>{scan.stationName}</td>
                        <td className='pr-4'>
                          {getDateString(scan.created, ['date'])}
                        </td>
                        <td className='pr-4'>
                          {getDateString(scan.created, ['time'])}
                        </td>
                        <td className='pr-4'>{dollarAmount(scan.tripCost)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            <table
              //* Scans - MOBILE View
              className='sm:hidden'
            >
              {selectedCardScansPaginated?.map((scan, i) => {
                return (
                  <tbody key={i} className='border-t first:border-t-0'>
                    <tr>
                      <td className='px-2 pt-2 font-semibold align-top'>
                        {localeText('rw_title_fare_product', 'Fare Product')}
                      </td>
                      <td className='px-2 pt-2 align-top'>
                        {getTicketName(scan.productId)}
                      </td>
                    </tr>
                    <tr>
                      <td className='px-2 font-semibold align-top'>
                        {localeText('rw_title_location', 'Location')}
                      </td>
                      <td className='px-2 align-top'>{scan.stationName}</td>
                    </tr>
                    <tr>
                      <td className='px-2 font-semibold align-top'>
                        {localeText('rw_title_date', 'Date')}/
                        {localeText('rw_title_time', 'Time')}
                      </td>
                      <td className='px-2 align-top'>
                        {' '}
                        {getDateString(scan.created)}
                      </td>
                    </tr>
                    <tr>
                      <td className='px-2 pb-3 font-semibold align-top'>
                        {localeText('rw_title_amount', 'Amount')}
                      </td>
                      <td className='px-2 pb-3 align-top'>
                        {dollarAmount(scan.tripCost)}
                      </td>
                    </tr>
                  </tbody>
                );
              })}
            </table>
            <Pagination
              {...{
                list: selectedCard?.scans,
                setPaginatedList: setSelectedCardScansPaginated,
                itemsPerPage: SELECTED_CARD_SCAN_PER_PAGE
              }}
            />
          </div>
        </Section>
      </div>
    );
  }

  const selectCardHandler = transitCardUuid => {
    if (transitCardUuid !== 'add') {
      setSelectedCard(
        account?.transitAccounts?.[0].transitCards?.find(
          tc => tc.uuid === transitCardUuid
        )
      );
    } else {
      setNewTransitCardNumber('');
      setSelectedCard({uuid: 'add'});
    }
  };

  return (
    <div>
      <Section
        sectionId='manage-cards'
        heading={localeText('rw_header_cards', 'Cards')}
      >
        <div className='flex gap-8 flex-col lg:flex-row'>
          <LabelAndInput
            //* Transit Cards - Mobile View
            inputId='select-transit-card-mobile'
            name='select transit card mobile'
            className='w-full lg:hidden'
            label={localeText('rw_title_card', 'Card')}
            type='select'
            id='selected-transitcard-uuid-mobile'
            onChange={e => selectCardHandler(e.target.value)}
            value={selectedCard?.uuid}
          >
            {account &&
              account.transitAccounts[0].transitCards.map(transitCard => {
                return (
                  <option key={transitCard.uuid} value={transitCard.uuid}>
                    {transitCard.nickname}
                  </option>
                );
              })}
            <option key='add' value='add'>
              {localeText('rw_button_register_new_card', 'Register New Card')}
            </option>
          </LabelAndInput>
          <div
            //* Transit Cards - DESKTOP View
            className='hidden lg:block'
          >
            {account?.transitAccounts?.[0].transitCards?.map(
              (transitCard, i) => (
                <Card
                  className={`w-48 cursor-pointer ${
                    selectedCard?.id === transitCard.id ? 'bg-secondary' : ''
                  }`}
                  onClick={() => selectCardHandler(transitCard.uuid)}
                  key={i}
                >
                  {transitCard.nickname}
                </Card>
              )
            )}
            <Card
              cardId='register-new-transit-card'
              className={`w-48 cursor-pointer ${
                selectedCard?.uuid === 'add' ? 'bg-secondary' : ''
              }`}
              onClick={() => selectCardHandler('add')}
            >
              {localeText('rw_button_register_new_card', 'Register New Card')}
            </Card>
          </div>
          {manageCardsRightSide}
        </div>
      </Section>
    </div>
  );
}
